// src/components/legal/CryptCrawlerPrivacyPolicy.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 3rem 2rem;
    line-height: 1.8;
    color: #333;
`;

const Header = styled.header`
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #eee;
`;

const Title = styled.h1`
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: #9B2C2C;
`;

const UpdateDate = styled.p`
    color: #666;
    font-size: 1rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    color: #9B2C2C;
    text-decoration: none;
    margin-bottom: 2rem;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.8;
    }

    &::before {
        content: '←';
        margin-right: 0.5rem;
    }
`;

const Section = styled.section`
    margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #444;
`;

const Paragraph = styled.p`
    margin-bottom: 1rem;
    text-align: justify;
    white-space: pre-wrap;
`;

const CompanyInfo = styled.div`
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 10px;
    margin-bottom: 2rem;
    white-space: pre-wrap;
`;

const InfoBox = styled.div`
    background: #FBEDED;
    padding: 1.5rem;
    border-radius: 10px;
    margin: 2rem 0;
    white-space: pre-wrap;
`;

const CryptCrawlerPrivacyPolicy = () => {
    return (
        <Container>
            <BackLink to="/cryptcrawler">CryptCrawler 홈으로 / Back to CryptCrawler</BackLink>

            <Header>
                <Title>CryptCrawler Privacy Policy / 개인정보처리방침</Title>
                <UpdateDate>Last updated / 최종 수정일: October 13, 2026</UpdateDate>
            </Header>

            <CompanyInfo>
                <SectionTitle>Data Controller / 데이터 컨트롤러 정보</SectionTitle>
                <Paragraph>
                    • Company / 회사명: MindWorld (마인드월드)
                    • Representative / 대표자: 윤우재
                    • Address / 주소: 경기도 의왕시 시청1로 11, Republic of Korea
                    • Business Registration / 사업자등록번호: 504-43-28680
                    • Email: mind-wellness@naver.com
                    • Service / 서비스: CryptCrawler mobile game (Android / iOS)
                    • Package ID: com.mindworld.cryptcrawler
                </Paragraph>
            </CompanyInfo>

            <Section>
                <SectionTitle>1. Overview / 개요</SectionTitle>
                <Paragraph>
                    MindWorld ("we", "us", "Company") respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you use CryptCrawler, a single-player roguelike deck-building dungeon crawler. This policy is drafted to comply with the EU General Data Protection Regulation (GDPR), the UK GDPR, the California Consumer Privacy Act (CCPA/CPRA), the Korean Personal Information Protection Act (PIPA), and the Children's Online Privacy Protection Act (COPPA).

                    마인드월드(이하 "회사")는 사용자의 개인정보 보호를 매우 중요하게 생각합니다. 본 개인정보처리방침은 싱글 플레이 로그라이크 덱빌딩 던전 게임 CryptCrawler(이하 "게임")를 이용할 때 어떤 정보가 수집·이용·보호되는지 설명하며, GDPR, 영국 GDPR, CCPA/CPRA, 한국 개인정보보호법, COPPA 등 관련 법규를 준수하여 작성되었습니다.

                    By installing or using CryptCrawler, you acknowledge that you have read and understood this policy.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>2. Legal Basis for Processing (GDPR) / 처리의 법적 근거</SectionTitle>
                <Paragraph>
                    Under GDPR Article 6, we process personal data on the following legal bases:

                    • Performance of a contract (Art. 6(1)(b)): Providing the game and processing in-app purchases.
                    • Legitimate interests (Art. 6(1)(f)): Service security, fraud prevention, and non-personalized advertising.
                    • Consent (Art. 6(1)(a)): Personalized advertising and tracking identifiers, where consent is required by law (EEA, UK, Switzerland).
                    • Legal obligation (Art. 6(1)(c)): Tax records, fraud reporting, lawful requests.

                    You may withdraw consent at any time through your device's advertising settings. Withdrawal does not affect the lawfulness of prior processing.

                    동의 철회: 기기의 광고 설정에서 언제든지 동의를 철회하실 수 있습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>3. Information We Collect / 수집하는 정보</SectionTitle>
                <Paragraph>
                    CryptCrawler has no account registration, no login, no cloud save, and no online multiplayer. The Company does not operate any server that receives your game data. We do not collect your name, email address, phone number, contacts, photos, or precise location.

                    Locally stored on your device (never transmitted to us):
                    • Account progress: unlocked classes, acts and cards, card mastery, achievements, statistics
                    • Currencies: platinum (permanent) and in-run gold
                    • Current run state (so you can continue an unfinished run)
                    • Settings: language, sound, music, vibration, battle speed
                    • Purchase entitlements cached locally (e.g., full version, ad removal)
                    This data is stored in the app's private storage (save files and PlayerPrefs), including automatic local backups used to recover a damaged save.

                    Collected automatically by third-party SDKs (advertising, purchases & diagnostics):
                    • Device identifiers: Advertising ID (Android Advertising ID / Apple IDFA) and app-set identifiers used by the ad SDK
                    • Device information: model, OS version, language, country, screen resolution
                    • IP address
                    • Ad interactions: ad requests, impressions, clicks, rewarded-ad completions
                    • Crash and diagnostic data collected by the SDKs
                    • Purchase information: product ID, transaction ID and receipt, handled by the platform store

                    Provided by you (optional):
                    • Customer support emails and the contents of those messages

                    기기 로컬 저장 정보 (회사로 전송되지 않음):
                    • 해금(직업·막·카드), 카드 숙련도, 업적, 통계, 백금·골드, 진행 중인 런, 설정, 구매 상태 캐시
                    • 저장 파일 손상 복구용 로컬 백업 포함

                    제3자 SDK 자동 수집 정보 (광고·결제·진단):
                    • 광고 식별자(AAID/IDFA), 기기 정보, IP 주소, 광고 상호작용, 충돌·진단 정보, 결제 상품 ID·거래 ID·영수증(플랫폼 스토어 처리)

                    회사는 회원가입·로그인·클라우드 저장·온라인 대전 기능을 운영하지 않으며, 이름·이메일·전화번호·연락처·사진·정밀 위치를 수집하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>4. Purposes of Processing / 처리 목적</SectionTitle>
                <Paragraph>
                    • Provide the game and save your progress on your device
                    • Display rewarded and interstitial video advertisements and grant in-game rewards
                    • Measure ad performance and prevent ad fraud
                    • Process in-app purchases and restore non-consumable purchases via the platform store
                    • Diagnose crashes and fix bugs
                    • Respond to customer support requests
                    • Comply with legal obligations (tax, accounting, lawful requests)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>5. Advertising and Third-Party SDKs / 광고 및 제3자 SDK</SectionTitle>
                <Paragraph>
                    CryptCrawler is supported by advertisements served through Google AdMob (Google LLC). The game shows optional rewarded video ads that you choose to watch in exchange for in-game rewards, and may show interstitial ads at natural breaks. Purchasing "Remove Ads" or the full version removes non-rewarded ads.

                    Third-party processors:
                    • Google AdMob (Google LLC / Google Ireland Limited) — advertising. See https://policies.google.com/privacy and https://policies.google.com/technologies/partner-sites
                    • Unity In-App Purchasing / Unity Gaming Services (Unity Technologies) — purchase processing layer. See https://unity.com/legal/game-player-and-app-user-privacy-policy
                    • Google Play Billing / Apple App Store — process in-app purchases. See https://policies.google.com/privacy and https://www.apple.com/legal/privacy

                    Personalized ads:
                    • EEA/UK/Switzerland: personalized ads are shown only where consent has been obtained as required by law; otherwise non-personalized ads are served.
                    • Other regions (including US/Korea): subject to your device-level "Opt out of Ads Personalization" / "Limit Ad Tracking" setting.

                    광고: Google AdMob을 통해 보상형 광고(선택 시청)와 전면 광고가 표시될 수 있으며, "광고 제거" 또는 정식판 구매 시 보상형이 아닌 광고는 표시되지 않습니다. 자세한 수집 항목은 위 링크를 참조하세요.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>6. Data Retention / 보유 기간</SectionTitle>
                <Paragraph>
                    • Game data on your device: until you uninstall the app or clear app data.
                    • Advertising identifiers and ad events: per Google AdMob's retention policy, then aggregated or deleted.
                    • Purchase records (Korean Electronic Commerce Act and tax law): 5 years, held by the platform store and, where applicable, by us.
                    • Customer support correspondence: up to 3 years after last contact.

                    보유 기간: 기기 데이터는 앱 삭제 시까지, 광고 데이터는 Google AdMob 정책에 따름, 결제 기록 5년(전자상거래법·국세기본법), 고객 문의 기록은 마지막 연락 후 최대 3년.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>7. International Data Transfers / 국제 전송</SectionTitle>
                <Paragraph>
                    Our service providers (Google and Unity) may process data in the United States and other countries outside the EEA/UK and Korea. Safeguards include the EU-US Data Privacy Framework, Standard Contractual Clauses (including the UK Addendum), and encryption in transit.

                    For Korean users, transfers to overseas processors are disclosed pursuant to Article 28-8 of PIPA:
                    • Recipients: Google LLC (USA), Unity Technologies (USA)
                    • Items: advertising ID, device information, IP address, ad interaction data, purchase transaction data
                    • Timing and method: transmitted over the network when ads are requested or a purchase is made
                    • Purpose: ad serving and measurement, purchase processing
                    • Retention: per each provider's policy
                    You may limit the transfer by opting out of ad personalization or purchasing ad removal; core gameplay remains available.

                    국외 이전: Google LLC·Unity Technologies(미국)로 광고 ID·기기 정보·IP·광고 상호작용·결제 거래 정보가 광고 요청·결제 시 네트워크를 통해 이전되며, 보유 기간은 각 사업자 정책에 따릅니다. 광고 개인화 해제 또는 광고 제거 구매로 이전을 제한할 수 있으며, 게임 핵심 기능 이용에는 영향이 없습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>8. Sharing of Information / 제3자 제공</SectionTitle>
                <Paragraph>
                    We do not sell your personal information. Data is shared only with:

                    • The service providers listed in Section 5, acting under their data processing terms
                    • Law enforcement or government authorities when legally required
                    • Parties to a corporate transaction (merger, acquisition, asset sale) with continued protection guarantees

                    회사는 사용자의 개인정보를 판매하지 않으며, 위 5항의 처리자에게만 각 사의 데이터 처리 약관에 따라 제공됩니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>9. Your Rights (GDPR / UK GDPR) / EU·영국 사용자의 권리</SectionTitle>
                <Paragraph>
                    If you are in the EEA, UK, or Switzerland, you have the right of access, rectification, erasure, restriction, data portability, objection, not to be subject to solely automated decisions, withdrawal of consent, and to lodge a complaint with your supervisory authority.

                    Because we do not hold your game data on any server, most requests concern data held by our advertising and purchase processors. To exercise your rights, email mind-wellness@naver.com with your Advertising ID (Android: Settings → Google → Ads) and, for purchases, your store Order ID. We respond within 30 days.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>10. California Residents' Rights (CCPA/CPRA) / 캘리포니아 사용자</SectionTitle>
                <Paragraph>
                    California residents have the right to know, delete, and correct personal information, to opt out of "sale" or "sharing" for cross-context behavioral advertising (we do not sell; advertising identifiers may be considered "sharing" — use your device's ad personalization opt-out), to limit use of sensitive personal information, and to non-discrimination.

                    To exercise these rights, email mind-wellness@naver.com.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>11. Korean Users / 한국 사용자</SectionTitle>
                <Paragraph>
                    한국 개인정보보호법(PIPA)에 따른 권리:

                    • 개인정보 열람, 정정, 삭제, 처리정지 요구권
                    • 손해배상청구권
                    • 동의 철회권

                    개인정보 보호 책임자:
                    • 성명: 윤우재
                    • 이메일: mind-wellness@naver.com

                    분쟁 조정 및 신고:
                    • 개인정보분쟁조정위원회: 1833-6972 (privacy.go.kr)
                    • 개인정보침해신고센터: 118 (privacy.kisa.or.kr)
                    • 대검찰청 사이버수사과: 1301 (spo.go.kr)
                    • 경찰청 사이버수사국: 182 (police.go.kr)
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>12. Children's Privacy / 아동 개인정보</SectionTitle>
                <Paragraph>
                    CryptCrawler is intended for users aged 13 and older and is not directed at children. We do not knowingly collect personal information from children under 13 (under 16 in some EEA jurisdictions).

                    If you believe a child has provided personal data through the game, contact mind-wellness@naver.com and we will act promptly.

                    아동 보호: 본 게임은 만 13세 이상을 대상으로 하며, 만 14세 미만 한국 사용자의 개인정보를 법정대리인 동의 없이 수집하지 않습니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>13. Data Security / 데이터 보안</SectionTitle>
                <Paragraph>
                    Game data is stored in the app's private storage on your device. Communication between the SDKs and their servers uses TLS encryption. We apply data minimization and do not operate our own server for player data.

                    In the event of a personal data breach likely to result in a high risk to your rights, we will notify the relevant supervisory authority within 72 hours where required, and affected users without undue delay.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>14. Cookies and Similar Technologies / 쿠키 및 유사 기술</SectionTitle>
                <Paragraph>
                    The game itself does not use HTTP cookies. The embedded ad SDK (Google AdMob) may use advertising identifiers and local storage equivalents to deliver and measure ads. You can reset or limit these in your device settings:

                    • Android: Settings → Google → Ads → Delete advertising ID / Opt out of Ads Personalization
                    • iOS: Settings → Privacy & Security → Tracking / Apple Advertising
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>15. Changes to This Policy / 정책 변경</SectionTitle>
                <Paragraph>
                    We may update this Privacy Policy from time to time. Material changes will be announced in-app or on this page at least 30 days before they take effect. Your continued use after the effective date constitutes acceptance of the updated policy.

                    중요 변경사항은 시행 30일 전 앱 내 또는 본 페이지에 공지합니다.
                </Paragraph>
            </Section>

            <Section>
                <SectionTitle>16. Contact / 연락처</SectionTitle>
                <Paragraph>
                    Data Protection Contact / 개인정보 보호 책임자:
                    • Name: 윤우재 (Yoon Woo-jae)
                    • Email: mind-wellness@naver.com
                    • Postal address: 경기도 의왕시 시청1로 11, Republic of Korea

                    EU Representative (Art. 27 GDPR): Not yet appointed. EU users may contact us directly at the email above.

                    We aim to respond to all privacy requests within 30 days.
                </Paragraph>
            </Section>

            <InfoBox>
                <SectionTitle>Policy Information / 방침 정보</SectionTitle>
                <Paragraph>
                    • Originally published / 최초 작성일: 2026-10-13
                    • Last updated / 최종 수정일: 2026-10-13
                    • Effective / 시행일: 2026-10-13
                    • Version / 버전: 1.0
                    • Applicable laws / 적용 법규: GDPR, UK GDPR, CCPA/CPRA, PIPA (Korea), COPPA
                </Paragraph>
            </InfoBox>
        </Container>
    );
};

export default CryptCrawlerPrivacyPolicy;
